import Layout from '../../../components/Mobile/Layout'
import { Feature, Map as MapOl, View } from 'ol'

import TileLayer from 'ol/layer/Tile'
import OSM from 'ol/source/OSM'
import 'ol/ol.css'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { fromLonLat } from 'ol/proj'
import styles from './Map.module.css'
import MyLocation from '../../../assets/my-location.svg?react'
import CameraTransparent from '../../../assets/camera-transparent.svg?react'
import BlockFilters from '../../../components/Mobile/BlockFilters'
import { useQsFilters } from '../../../hooks/filters'
import VectorLayer from 'ol/layer/Vector'
import {
  clusterSource,
  getFeatureInfo,
  getFeatureStyle,
  vectorSource,
} from '../../../lib/MagikCluster'
import { useNavigate } from 'react-router-dom'
import { getActiveGeocontext, smartSlug, unicodeToChar } from '../../../utils'
import { useTranslation } from 'react-i18next'
import { Attribution, Zoom, ScaleLine } from 'ol/control'
import { Spinner } from 'react-bootstrap'
import { defaults } from 'ol/interaction/defaults'
import IconMonument from '../../../components/IconMonument'
import { useComuneById } from '../../../hooks/comuni'
import FiltersIcon from '../../../components/Icons/FiltersIcon'
import { Fill, Icon, Stroke, Style } from 'ol/style'
import VectorSource from 'ol/source/Vector'
import { MultiPolygon, Point, Polygon } from 'ol/geom'
import { useTopContextState } from '../../../context/TopContext'
import Popup from 'ol-popup'
import { MarkerProps } from '../../../types'
import { createPortal } from 'react-dom'
import ReactSwitch from 'react-switch'
import { usePaeseContext } from '../../../context/PaeseContext'
import classNames from 'classnames'
import { useProvinciaById } from '../../../hooks/province'
import { useRegioneById } from '../../../hooks/regioni'

const getFilters = (params: URLSearchParams) => ({
  search: params.get('search') ?? '',
  municipality: params.get('municipality') ?? '',
  region: params.get('region') ?? '',
  province: params.get('province') ?? '',
  app_category: params.get('app_category') ?? '',
  ordering: params.get('ordering') ?? '',
  in_contest: params.get('in_contest') ?? 'true',
  only_without_pictures: params.get('only_without_pictures') ?? '',
  user_lat: params.get('user_lat') ?? '',
  user_lon: params.get('user_lon') ?? '',
  monument_lon: Number(params.get('monument_lon')) ?? '',
  monument_lat: Number(params.get('monument_lat')) ?? '',
  geo_context: params.get('geo_context') ?? '',
  cluster_active: params.get('cluster_active') ?? 'true',
  without_coordinates: params.get('without_coordinates') ?? '',
  only_disabled: params.get('only_disabled') ?? '',
  last_image_before_5_years: params.get('last_image_before_5_years') ?? '',
})

const vectorSourceMyLocation = new VectorSource({
  features: [],
})

const myLocationLayer = new VectorLayer({
  source: vectorSourceMyLocation,
  style: new Style({
    image: new Icon({
      src: '/markers/my-position.png',
      scale: 0.2,
    }),
  }),
})

function setClusterDistance(resolution: number) {
  if (resolution < 0.1) {
    clusterSource.setDistance(0)
  } else if (resolution <= 300) {
    clusterSource.setDistance(40)
  } else {
    clusterSource.setDistance(0)
  }
}

const popup = new Popup({ stopEvent: true, positioning: 'bottom-center' })
const popupMarker = new Popup({
  stopEvent: false,
  positioning: 'bottom-center',
})

export default function Map() {
  const { filters, setFilters } = useQsFilters(getFilters)
  const mapElement = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<MapOl | null>(null)
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false)
  const activeGeocontext = getActiveGeocontext()
  const { data: comune, isLoading: isComuneLoading } = useComuneById(
    activeGeocontext?.id,
    Number(filters.municipality || '')
  )
  const { data: provincia, isLoading: isProvinciaLoading } = useProvinciaById(
    activeGeocontext?.id,
    Number(filters.province || '')
  )

  const { data: regione, isLoading: isRegioneLoading } = useRegioneById(
    activeGeocontext?.id,
    Number(filters.region || '')
  )

  const boundarySource = useMemo(() => new VectorSource({ features: [] }), [])

  const boundaryLayer = useMemo(
    () =>
      new VectorLayer({
        source: boundarySource,
        // opzionale: zIndex basso (non guasta)
        zIndex: 1,
        style: new Style({
          stroke: new Stroke({ color: '#FF4D00', width: 2 }),
          fill: new Fill({ color: '#FF4D001A' }),
        }),
      }),
    [boundarySource]
  )

  const navigate = useNavigate()
  const { i18n, t } = useTranslation()
  const [loading, setLoading] = useState<boolean>(false)
  const [comuneFilterCoords, setComuneFilterCoords] = useState<number[] | null>(
    null
  )
  const [infoMarker, setInfoMarker] = useState<MarkerProps | null>(null)
  const [markersCluster, setMarkersCluster] = useState<MarkerProps[] | null>(
    null
  )

  const [mapState, setMapState] = useState({
    center: fromLonLat(
      activeGeocontext?.centroid.coordinates || [12.496366, 41.902782]
    ),
    zoom: activeGeocontext?.zoom_level || 6,
    maxZoom: 22,
    minZoom: 2,
  })

  const { geoPermission } = useTopContextState()

  function handleLocationClick() {
    if (navigator.geolocation && geoPermission !== 'denied') {
      navigator.geolocation.getCurrentPosition(success, error)
    } else {
      console.log('Geolocation not supported')
    }
  }

  function success(position: any) {
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    const featureMyLocation = new Feature({
      geometry: new Point(fromLonLat([longitude, latitude])),
    })
    vectorSourceMyLocation.clear()
    vectorSourceMyLocation.addFeature(featureMyLocation)
    setFilters({
      ...filters,
      user_lat: latitude,
      user_lon: longitude,
    })
    setMapState({
      ...mapState,
      center: fromLonLat([longitude, latitude]),
      zoom: 16,
    })
  }

  function error() {
    console.log('Unable to retrieve your location')
  }

  const filtersForRefresh = useMemo(() => {
    const o = {
      search: filters.search,
      municipality: filters.municipality,
      province: filters.province,
      region: filters.region,
      ordering: filters.ordering,
      in_contest: filters.in_contest,
      only_without_pictures: filters.only_without_pictures,
      app_category: filters.app_category,
      user_lat: filters.user_lat,
      user_lon: filters.user_lon,
      geo_context: filters.geo_context,
      cluster_active: filters.cluster_active,
      without_coordinates: filters.without_coordinates,
      only_disabled: filters.only_disabled,
      last_image_before_5_years: filters.last_image_before_5_years,
    }
    return Object.values(o).join('')
  }, [filters])

  useEffect(() => {
    vectorSource.set('filters', filters)
    vectorSource.refresh()
  }, [filtersForRefresh])

  useEffect(() => {
    if (!mapElement.current || !activeGeocontext) return

    vectorSource.set('setLoading', setLoading)

    const categories = activeGeocontext?.app_categories

    vectorSource.set('categories', categories)

    const featureOverlay = new VectorLayer({
      source: clusterSource,
      style: getFeatureStyle,
    })

    featureOverlay.setZIndex(10)
    myLocationLayer.setZIndex(11)
    boundaryLayer.setZIndex(1)

    const interactions = defaults({
      altShiftDragRotate: false,
      pinchRotate: false,
    })

    const initialMap = new MapOl({
      target: mapElement.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
        boundaryLayer,
        featureOverlay,
        myLocationLayer,
      ],
      interactions: interactions,
      overlays: [popup, popupMarker],
      controls: [
        new Zoom({
          zoomInClassName: styles.ZoomIn,
          zoomOutClassName: styles.ZoomOut,
        }),
        new Attribution({
          collapsible: false,
        }),
        new ScaleLine({
          units: 'metric',
        }),
      ],
      view: new View(mapState),
    })

    initialMap.on('click', function (evt) {
      let shouldCloseMarker = true
      let shouldCloseCluster = true

      if (evt.originalEvent.target.classList.contains('avoid-clicks')) {
        return
      }

      initialMap.forEachFeatureAtPixel(
        evt.pixel,
        function (feature, layer) {
          if (layer?.get('nonInteractive')) {
            return false
          }

          if (layer === boundaryLayer) {
            return false
          }

          const info = getFeatureInfo(feature)
          if (info === 1) {
            setMarkersCluster(null)
            popupMarker.hide()
            const monument = feature.getProperties().features[0].getProperties()
            const appCategory = feature
              .getProperties()
              .features[0].getProperties().app_category
            sessionStorage.setItem(
              'map_state',
              JSON.stringify({
                center: map?.getView().getCenter(),
                zoom: map?.getView().getZoom(),
              })
            )
            initialMap?.getView().animate({
              center: fromLonLat([
                monument.position.coordinates[0],
                monument.position.coordinates[1],
              ]),
              // zoom: initialMap?.getView().getZoom(),
              duration: 500,
            })
            if (infoMarker) {
              setInfoMarker(null)
              setPopOpen(null)
            }

            setInfoMarker({
              id: monument.id,
              label: monument.label,
              pictures_wlm_count: monument.pictures_wlm_count,
              pictures_count: monument.pictures_count,
              coords: evt.pixel,
              coordinate: evt.coordinate,
              app_category: appCategory,
              in_contest: monument.in_contest,
              feature: feature,
            })
            shouldCloseMarker = false
          } else if (info > 1) {
            setInfoMarker(null)

            // check if all the elements in the cluster are in the same position coordinates
            const subFeatures = feature.getProperties().features
            const subFeaturesWithId = subFeatures
              .map((f: any) => {
                const monument = f.getProperties()
                if (monument.id) {
                  return true
                }
                return false
              })
              .filter((f: any) => f)

            const sameCoordinates = subFeatures.every(
              (f: any) =>
                f.getProperties().position?.coordinates[0] ===
                  subFeatures[0].getProperties().position?.coordinates[0] &&
                f.getProperties().position?.coordinates[1] ===
                  subFeatures[0].getProperties().position?.coordinates[1]
            )

            if (sameCoordinates && subFeaturesWithId.length > 0) {
              // zoom in
              const currentZoom = initialMap?.getView().getZoom()
              initialMap?.getView().animate({
                center: evt.coordinate,
                zoom: currentZoom ? currentZoom + 1 : 16,
                duration: 500,
              })

              setMarkersCluster(null)

              // show info marker with list of monuments
              for (const f of subFeatures) {
                const monument = f.getProperties()
                setMarkersCluster((prev) => [
                  ...(prev || []),
                  {
                    id: monument.id,
                    label: monument.label,
                    pictures_wlm_count: monument.pictures_wlm_count,
                    pictures_count: monument.pictures_count,
                    coords: evt.pixel,
                    coordinate: evt.coordinate,
                    app_category: monument.app_category,
                    in_contest: monument.in_contest,
                    feature: f,
                  },
                ])
              }

              shouldCloseCluster = false
            } else {
              const currentZoom = initialMap?.getView().getZoom()
              initialMap?.getView().animate({
                center: evt.coordinate,
                zoom: currentZoom ? currentZoom + 1 : 16,
                duration: 500,
              })
            }
          }
        },
        {
          hitTolerance: 30,
        }
      )
      if (shouldCloseMarker) {
        setInfoMarker(null)
        // setDetail(null)
      }

      if (shouldCloseCluster) {
        setMarkersCluster(null)
      }
    })

    setMap(initialMap)
    return () => initialMap.setTarget(undefined as unknown as HTMLElement)
  }, [mapState, activeGeocontext, i18n.language])

  useEffect(() => {
    if (!map) return
    const v = map.getView()
    const zoom = v.getZoom()
    const r = v.getResolution()
    if (zoom && zoom > 11 && filters.cluster_active === '') {
      clusterSource.setDistance(0)
    } else {
      setClusterDistance(r || 1000)
    }

    v.on('change', function () {
      const resolution = v.getResolution() || 1000
      const zoom = v.getZoom()
      if (zoom && zoom > 11 && filters.cluster_active === '') {
        clusterSource.setDistance(0)
      } else {
        setClusterDistance(resolution)
      }
    })
  }, [map, filters.cluster_active])

  useEffect(() => {
    if (!map) return
    map.updateSize()
  }, [map])

  useEffect(() => {
    if (
      filters.monument_lat &&
      filters.monument_lat !== 0 &&
      filters.monument_lon &&
      filters.monument_lon !== 0
    ) {
      const center = fromLonLat([filters.monument_lon, filters.monument_lat])
      const zoom = 15

      map?.getView().animate({
        center,
        zoom,
        duration: 500,
      })

      setMapState((currentMapState) => {
        if (
          currentMapState.center[0] === center[0] &&
          currentMapState.center[1] === center[1] &&
          currentMapState.zoom === zoom
        ) {
          return currentMapState
        }

        return {
          ...currentMapState,
          center,
          zoom,
        }
      })
    }
  }, [filters.monument_lat, filters.monument_lon, map])

  const areFiltersActive = useMemo(() => {
    if (
      filters.app_category !== '' ||
      filters.in_contest !== 'true' ||
      filters.municipality !== '' ||
      filters.province !== '' ||
      filters.region !== '' ||
      filters.only_without_pictures !== '' ||
      filters.search !== '' ||
      filters.last_image_before_5_years !== '' ||
      filters.without_coordinates !== '' ||
      filters.only_disabled !== ''
    ) {
      return true
    } else {
      return false
    }
  }, [filters])

  useEffect(() => {
    if (!comuneFilterCoords || !map) return

    const center = fromLonLat(comuneFilterCoords)
    const zoom = 12

    map.getView().animate({
      center,
      zoom,
      duration: 1000,
    })
    setComuneFilterCoords(null)
  }, [comuneFilterCoords, map])

  useEffect(() => {
    const municipalityId = Number(filters.municipality || 0)

    if (
      !map ||
      !municipalityId ||
      isComuneLoading ||
      !comune ||
      Number(comune.id) !== municipalityId ||
      (filters.monument_lat && filters.monument_lat !== 0) ||
      (filters.monument_lon && filters.monument_lon !== 0)
    ) {
      return
    }

    const coordinates = comune.centroid.coordinates
    if (!coordinates) return

    const center = fromLonLat([coordinates[0], coordinates[1]])
    const zoom = 12

    map.getView().animate({
      center,
      zoom,
      duration: 1000,
    })
  }, [
    filters.municipality,
    filters.monument_lat,
    filters.monument_lon,
    comune,
    isComuneLoading,
    map,
  ])

  useEffect(() => {
    const provinceId = Number(filters.province || 0)

    if (
      !map ||
      !provinceId ||
      isProvinciaLoading ||
      !provincia ||
      Number(provincia.id) !== provinceId ||
      (filters.monument_lat && filters.monument_lat !== 0) ||
      (filters.monument_lon && filters.monument_lon !== 0)
    ) {
      return
    }

    const coordinates = provincia.centroid.coordinates
    if (!coordinates) return

    map.getView().animate({
      center: fromLonLat([coordinates[0], coordinates[1]]),
      zoom: 14,
      duration: 500,
    })
  }, [
    filters.province,
    filters.monument_lat,
    filters.monument_lon,
    provincia,
    isProvinciaLoading,
    map,
  ])

  useEffect(() => {
    const regionId = Number(filters.region || 0)

    if (
      !map ||
      filters.province ||
      filters.municipality ||
      !regionId ||
      isRegioneLoading ||
      !regione ||
      Number(regione.id) !== regionId ||
      (filters.monument_lat && filters.monument_lat !== 0) ||
      (filters.monument_lon && filters.monument_lon !== 0)
    ) {
      return
    }

    const coordinates = regione.centroid.coordinates
    if (!coordinates) return

    map.getView().animate({
      center: fromLonLat([coordinates[0], coordinates[1]]),
      zoom: 12,
      duration: 500,
    })
  }, [
    filters.region,
    filters.province,
    filters.municipality,
    filters.monument_lat,
    filters.monument_lon,
    regione,
    isRegioneLoading,
    map,
  ])

  const popupRef = useRef<HTMLDivElement>(null)
  const markersClusterRef = useRef<HTMLDivElement>(null)

  const setDetail = useCallback(() => {
    if (infoMarker) {
      sessionStorage.setItem(
        'map_state',
        JSON.stringify({
          center: map?.getView().getCenter(),
          zoom: map?.getView().getZoom(),
        })
      )
      navigate(
        `/${i18n.language}/mappa/${smartSlug(
          infoMarker.id,
          infoMarker.label
        )}?${new URLSearchParams({
          search: filters.municipality ? filters.search : '',
          municipality: filters.municipality,
          province: filters.province,
          region: filters.region,
          app_category: filters.app_category,
          in_contest: filters.in_contest,
          only_without_pictures: filters.only_without_pictures,
          user_lat: String(filters.user_lat),
          user_lon: String(filters.user_lon),
          ordering: filters.ordering,
          monument_lat: String(filters.monument_lat) || '',
          monument_lon: String(filters.monument_lon) || '',
          geo_context: filters.geo_context,
          without_coordinates: filters.without_coordinates,
          only_disabled: filters.only_disabled,
          last_image_before_5_years: filters.last_image_before_5_years,
        })}`
      )
    }
  }, [infoMarker, filters, i18n.language, navigate, map])

  const [popOpen, setPopOpen] = useState<string | null>(null)

  useEffect(() => {
    if (infoMarker && popupRef.current) {
      popup.show(
        infoMarker.feature.getGeometry().getCoordinates(),
        '<div></div>'
      )
      setPopOpen(infoMarker.id.toString())
    } else {
      popup.hide()
      setPopOpen(null)
    }
  }, [infoMarker, setDetail])

  useEffect(() => {
    if (markersCluster) {
      popupMarker.show(
        markersCluster[0].feature.getGeometry().getCoordinates(),
        '<div></div>'
      )
    } else {
      popupMarker.hide()
    }
  }, [markersCluster, popupMarker])

  useEffect(() => {
    if (
      sessionStorage.getItem('map_state') &&
      filters.monument_lat !== 0 &&
      filters.monument_lon !== 0
    ) {
      const mapState = JSON.parse(sessionStorage.getItem('map_state')!)
      map?.getView().setZoom(mapState.zoom)
      map?.getView().setCenter(mapState.center)
      setMapState(mapState)
      setTimeout(() => {
        sessionStorage.removeItem('map_state')
      }, 1000)
    }
  }, [])

  useEffect(() => {
    if (sessionStorage.getItem('monument')) {
      const monument = JSON.parse(sessionStorage.getItem('monument')!)
      setMapState({
        ...mapState,
        center: fromLonLat([
          monument.position.coordinates[0],
          monument.position.coordinates[1],
        ]),
        zoom: 20,
      })
      sessionStorage.removeItem('monument')
    }
  }, [])

  useEffect(() => {
    map?.on('moveend', function (e) {
      const zoom = map.getView().getZoom()
      const center = map.getView().getCenter()
      sessionStorage.setItem('map_state', JSON.stringify({ center, zoom }))
    })
  }, [map])

  useEffect(() => {
    if (
      sessionStorage.getItem('map_state') &&
      !filters.monument_lat &&
      !filters.monument_lon &&
      !filters.municipality &&
      !filters.province &&
      !filters.region
    ) {
      const mapState = JSON.parse(sessionStorage.getItem('map_state')!)
      if (mapState) {
        map?.getView().setCenter(mapState.center)
        map?.getView().setZoom(mapState.zoom)
        setMapState(mapState)
      }
    }
  }, [])

  const [switchCluster, setSwitchCluster] = useState(false)

  useEffect(() => {
    if (!map) return
    const v = map?.getView()
    v.on('change', () => {
      const zoom = v.getZoom()
      if (zoom && zoom <= 10) {
        setSwitchCluster(false)
      } else {
        setSwitchCluster(true)
      }
    })
  }, [map])

  useEffect(() => {
    if (!activeGeocontext) return
    if (
      activeGeocontext &&
      filters.municipality === '' &&
      filters.province === '' &&
      filters.region === '' &&
      (!filters.monument_lat || filters.monument_lat === 0) &&
      (!filters.monument_lon || filters.monument_lon === 0) &&
      localStorage.getItem('trigger_paese')
    ) {
      map?.getView().animate({
        center: fromLonLat(activeGeocontext.centroid.coordinates),
        zoom: activeGeocontext.zoom_level,
        duration: 1000,
      })
      setMapState({
        center: fromLonLat(activeGeocontext.centroid.coordinates),
        zoom: activeGeocontext.zoom_level,
        maxZoom: 22,
        minZoom: 2,
      })
      if (map) {
        localStorage.removeItem('trigger_paese')
      }
    }
  }, [activeGeocontext])

  const { country, setCountry } = usePaeseContext()
  const { geocontexts, isLoading: isLoadingGeocontexts } = useTopContextState()
  const navigation = useNavigate()

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const geoContextId = urlParams.get('geo_context')
    if (geocontexts.length > 0 && geoContextId) {
      const geocontext = geocontexts.find(
        (context) => context.id === parseInt(geoContextId)
      )
      if (geocontext) {
        setCountry({
          id: geocontext.id,
          label: geocontext.label,
        })
        localStorage.setItem(
          'paese',
          JSON.stringify({
            id: geocontext.id,
            label: geocontext.label,
          })
        )
        localStorage.setItem(
          'trigger_paese',
          JSON.stringify({
            id: geocontext.id,
            label: geocontext.label,
          })
        )
      }
    }
    if (!country?.id && !isLoadingGeocontexts) {
      navigation('/' + i18n.language + '/')
    }
  }, [country?.id, geocontexts, isLoadingGeocontexts])

  useEffect(() => {
    if (!map) return

    // pulisci sempre subito
    boundarySource.clear()

    const regionId = Number(filters.region || 0)
    const comuneId = Number(filters.municipality || 0)
    const provinciaId = Number(filters.province || 0)

    // ✅ Regione: disegna SOLO se la regione caricata è quella attualmente selezionata
    if (
      regionId &&
      !filters.province &&
      !filters.municipality &&
      !isRegioneLoading &&
      regione &&
      Number(regione.id) === regionId && // 👈 guard fondamentale
      regione?.poly?.type &&
      regione?.poly?.coordinates
    ) {
      const { type, coordinates } = regione.poly

      let geom: Polygon | MultiPolygon | null = null
      if (type === 'Polygon') geom = new Polygon(coordinates)
      else if (type === 'MultiPolygon') geom = new MultiPolygon(coordinates)

      if (!geom) return

      geom.transform('EPSG:4326', 'EPSG:3857')
      boundarySource.addFeature(new Feature({ geometry: geom }))
      return
    }

    // ✅ Provincia: stessa idea

    if (
      provinciaId &&
      !filters.municipality &&
      !isProvinciaLoading &&
      provincia &&
      Number(provincia.id) === provinciaId && // 👈 guard
      provincia?.centroid?.coordinates
    ) {
      const { type, coordinates } = provincia.poly

      let geom: Polygon | MultiPolygon | null = null
      if (type === 'Polygon') geom = new Polygon(coordinates)
      else if (type === 'MultiPolygon') geom = new MultiPolygon(coordinates)

      if (!geom) return

      geom.transform('EPSG:4326', 'EPSG:3857')
      boundarySource.addFeature(new Feature({ geometry: geom }))
      return
    }

    // ✅ Comune: stessa idea
    if (
      comuneId &&
      !isComuneLoading &&
      comune &&
      Number(comune.id) === comuneId && // 👈 guard
      comune?.poly?.type &&
      comune?.poly?.coordinates
    ) {
      const { type, coordinates } = comune.poly

      let geom: Polygon | MultiPolygon | null = null
      if (type === 'Polygon') geom = new Polygon(coordinates)
      else if (type === 'MultiPolygon') geom = new MultiPolygon(coordinates)

      if (!geom) return

      geom.transform('EPSG:4326', 'EPSG:3857')
      boundarySource.addFeature(new Feature({ geometry: geom }))
      return
    }
  }, [
    map,
    filters.region,
    filters.province,
    filters.municipality,
    regione,
    comune,
    provincia,
    isRegioneLoading,
    isComuneLoading,
    isProvinciaLoading,
  ])

  useLayoutEffect(() => {
    boundarySource.clear()
  }, [filters.region, filters.province, filters.municipality])

  return (
    <Layout>
      <div className="w-100 h-100">
        <div ref={mapElement} id="map" className="w-100 h-100">
          <div
            className={
              areFiltersActive
                ? styles.ButtonFiltersActive
                : styles.ButtonFilters
            }
            onClick={() => {
              setFiltersOpen(!filtersOpen)
            }}
          >
            <FiltersIcon />
            {areFiltersActive && <div className={styles.Badge} />}
          </div>
          <div className={styles.ContainerButtons}>
            <div
              className={styles.ButtonResetItalia}
              onClick={() => {
                map?.getView().animate({
                  center: fromLonLat(
                    activeGeocontext?.centroid.coordinates as [number, number]
                  ),
                  zoom: activeGeocontext?.zoom_level,
                  duration: 1000,
                })
              }}
            >
              <span style={{ fontSize: 25 }}>
                {unicodeToChar(activeGeocontext?.flag)}
              </span>
            </div>
            <div className={styles.SwitchContainerCluster}>
              <label className={styles.LabelSwitch}>
                {t('MPL_BOX_CLUSTER')}
              </label>
              <ReactSwitch
                offColor="#D0DFE4"
                size={15}
                disabled={!switchCluster}
                checkedIcon={false}
                uncheckedIcon={false}
                onColor="#40BAEC"
                onChange={(checked) => {
                  setFilters({
                    ...filters,
                    cluster_active: checked ? 'true' : '',
                  })
                }}
                checked={filters.cluster_active === 'true'}
              />
            </div>
            <div
              className={styles.ButtonMyLocation}
              onClick={handleLocationClick}
            >
              <MyLocation />
            </div>
          </div>
        </div>
        {loading && (
          <div className={styles.LoadingButton}>
            <Spinner
              style={{
                color: 'var(--primary)',
              }}
              size="sm"
            />
          </div>
        )}
        <div
          ref={markersClusterRef}
          style={{
            position: 'absolute',
            top: '-12px',
          }}
        >
          {markersCluster &&
            // create portal for cluster with list of monuments
            markersCluster.length > 1 &&
            createPortal(
              <>
                <div
                  style={{
                    opacity: 1,
                    zIndex: 1,
                    backgroundColor: 'white',
                  }}
                  className={styles.DetailMarkerCluster}
                >
                  {markersCluster.map((marker, index) => (
                    <div
                      key={index}
                      className={classNames(
                        `${styles.MarkerCluster} py-2 pointer`,
                        {
                          'border-bottom': index !== markersCluster.length - 1,
                        }
                      )}
                    >
                      <div
                        style={{
                          width: 20,
                        }}
                      >
                        <IconMonument
                          monument={{
                            in_contest: marker.in_contest,
                            pictures_count: marker.pictures_count,
                            app_category: marker.app_category,
                          }}
                        />
                      </div>
                      {
                        <div
                          onClick={() => {
                            navigate(
                              `/${i18n.language}/mappa/${smartSlug(
                                marker.id,
                                marker.label
                              )}?${new URLSearchParams({
                                search: filters.municipality
                                  ? filters.search
                                  : '',
                                municipality: filters.municipality,
                                province: filters.province,
                                region: filters.region,
                                app_category: filters.app_category,
                                in_contest: filters.in_contest,
                                only_without_pictures:
                                  filters.only_without_pictures,
                                user_lat: String(filters.user_lat),
                                user_lon: String(filters.user_lon),
                                ordering: filters.ordering,
                                monument_lat:
                                  String(filters.monument_lat) || '',
                                monument_lon:
                                  String(filters.monument_lon) || '',
                                geo_context: filters.geo_context,
                                cluster_active: filters.cluster_active,
                                without_coordinates:
                                  filters.without_coordinates,
                                only_disabled: filters.only_disabled,
                                last_image_before_5_years:
                                  filters.last_image_before_5_years,
                              })}`
                            )
                          }}
                          className={`${styles.TitleMarkerCluster} avoid-clicks`}
                        >
                          {marker.label
                            ? marker.label.charAt(0).toUpperCase() +
                              marker.label.slice(1)
                            : ''}
                        </div>
                      }
                    </div>
                  ))}
                  <div
                    className={styles.PinMarker}
                    style={{
                      borderTop: '10px solid white',
                    }}
                  ></div>
                </div>
              </>,
              popupMarker.getElement() as HTMLElement
            )}
        </div>
        <div
          className="popup-container"
          ref={popupRef}
          style={{
            position: 'relative',
            top: '-12px',
          }}
        >
          {infoMarker &&
            createPortal(
              <>
                <div
                  onClick={(e) => {
                    if (popOpen === infoMarker.id.toString()) {
                      setDetail()
                    }
                  }}
                  style={{
                    zIndex: 1,
                    backgroundColor:
                      infoMarker.pictures_count === 0
                        ? 'var(--tertiary)'
                        : infoMarker.pictures_count > 0 &&
                            infoMarker.pictures_count <= 10
                          ? 'var(--monumento-poche-foto)'
                          : 'var(--monumento-tante-foto)',
                  }}
                  className={styles.DetailMarker}
                >
                  <div>
                    <IconMonument
                      monument={{
                        in_contest: infoMarker.in_contest,
                        pictures_count: infoMarker.pictures_count,
                        app_category: infoMarker.app_category,
                      }}
                    />
                  </div>
                  <div className={styles.TitleMarker}>{infoMarker.label}</div>
                  <div className={styles.TextMarker}>
                    <div>
                      <CameraTransparent />
                    </div>
                    <div className="ms-2 mt-1">{infoMarker.pictures_count}</div>
                  </div>
                  <div
                    className={styles.PinMarker}
                    style={{
                      borderTop:
                        '10px solid ' +
                        (infoMarker.pictures_count === 0
                          ? 'var(--tertiary)'
                          : infoMarker.pictures_count > 0 &&
                              infoMarker.pictures_count <= 10
                            ? 'var(--monumento-poche-foto)'
                            : 'var(--monumento-tante-foto)'),
                    }}
                  ></div>
                </div>
              </>,
              popup.getElement() as HTMLElement
            )}
        </div>
      </div>

      <BlockFilters
        filters={filters}
        setFilters={setFilters}
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        setComuneFilterCoords={setComuneFilterCoords}
      />
    </Layout>
  )
}
